import type { Variants } from "motion/react";
import { durations, easings, springs } from "./motion-tokens";

/** Fades in while rising slightly. General-purpose entrance. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: springs.gentle },
  exit: {
    opacity: 0,
    y: 8,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Fades in while dropping from above. Used for header-like chrome. */
export const fadeDown: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: springs.gentle },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Slides in from the left edge. Used for side-anchored buttons. */
export const slideFromLeft: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: springs.snappy },
  exit: {
    opacity: 0,
    x: -30,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Pops up with mass: rises, overshoots scale slightly, settles. */
export const popUp: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.85 },
  visible: { opacity: 1, y: 0, scale: 1, transition: springs.snappy },
  exit: {
    opacity: 0,
    y: 12,
    scale: 0.9,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Hero pop: the bounciest entrance, reserved for the single most important CTA. */
export const heroPop: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.8 },
  visible: { opacity: 1, y: 0, scale: 1, transition: springs.bouncy },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Simple opacity fade. Used for backdrops and low-emphasis chrome. */
export const fade: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.base, ease: easings.enter },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Quick, subtle opacity fade with no offset. Used when re-entering a screen
 * that has already played its full choreography once (e.g. Home after
 * returning from a sub-view), so navigation stays snappy. */
export const fadeFast: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.fast, ease: easings.enter },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast, ease: easings.exit },
  },
};

/** Root-level screen transition (route swaps). */
export const screenFade: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.slow, ease: easings.enter },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.base, ease: easings.exit },
  },
};

/**
 * Splash-specific exit: scales up slightly while fading, reading as the
 * camera pushing through the logo into the world behind it.
 */
export const splashTransition: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.fast, ease: easings.enter },
  },
  exit: {
    opacity: 0,
    scale: 1.06,
    transition: { duration: durations.base, ease: easings.exit },
  },
};

export const motionVariants = {
  fadeUp,
  fadeDown,
  slideFromLeft,
  popUp,
  heroPop,
  fade,
  fadeFast,
  screenFade,
  splashTransition,
} as const;

export type MotionVariantName = keyof typeof motionVariants;
